import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    CountDown,
    HowToBanner,
    ViewTemplate,
} from '../../components/sections';
import './onboarding-htv-library-view.scss';
import { useAppInformationContent } from '../../components/sections/breadcrumbs/hook/use-breadcrumb-content';
import { SecondaryButton } from '../../components/common';
import { VehicleAttributes } from '../../models/vehicle-attributes';
import RoadBlockPage from '../road-block-page/road-block-view';
import { useAnalytics } from '../../hooks/use-analytics';
import ServerContext from '../../contexts/serverContext';
import { popularVideos } from '../../services/onboarding-brightcove-video-service/popular-video-service';
import serverSideService from '../../services/server-side-service/server-side-service';
import useSeoConfig from '../../hooks/use-seo-config';
import siteMapService from '../../support/services/site-map-service/site-map-service';
import { useDyfEligibleModel } from '../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
import {
    getAemVehicleModelData,
    useAemVehicleModelData,
} from '../../hooks/use-aem-vehicle-model-data';
import { useServerData } from '../../hooks/use-server-data';
import { PageType } from '../../components/sections/view-template/page-type';
import { filterXSS } from 'xss';
import { useDyfQueryParams } from '../../hooks/dyf/use-dyf-query-params/use-dyf-query-params';
import { useFeatureGuideDaosAnalytics } from '../../hooks/dyf/use-feature-guide-daos-analytics/use-feature-guide-daos-analytics';
import { VideoLibraryCarousel } from '../../components/sections/onboarding-how-to-videos/video-library-carousel/video-library-carousel';
import { useHowToVideoContent } from '../../hooks/dyf/use-how-to-video-content/use-how-to-video-content';
import { DyfFeatureGuideBreadCrumbs } from '../../components/sections/dyf-landing/dyf-feature-guide-breadcrumbs/dyf-feature-guide-breadcrumbs';
import { useDyfFeatureGuideBreadcrumbsContent } from '../dyf-landing-view/hooks/use-dyf-feature-guide-breadcrumbs-content';
import useFooterDisplay from '../../hooks/use-footer-display';

const capitalizeYmm = (str: string) => {
    return str
        .split(' ')
        .map(e => e.charAt(0).toUpperCase() + e.slice(1))
        .join(' ');
};

export const HTVVideoLibraryView = () => {
    const serverContext = useContext(ServerContext);
    const {
        brand,
        currentRegionCode,
        currentLanguageRegionCode,
        root,
    } = serverContext;
    const [fireEvents] = useAnalytics();
    const [analyticsFired, setAnalyticsFired] = useState<boolean>(false);
    const baseRoot = root ? root.substring(0, root.length - 1) : '';

    const appInformationContent = useAppInformationContent();
    const howToVideoContent = useHowToVideoContent();
    const [
        categoryVideosResponseError,
        setCategoryVideosResponseError,
    ] = useState<boolean>(false);

    const page = useRef<PageType>('How To Video');

    const { queryModel, queryYear } = useDyfQueryParams();

    const serverDataCallbackMarketName = async () => {
        const vehicleYmm = await getAemVehicleModelData(
            brand,
            currentRegionCode,
            currentLanguageRegionCode,
            queryYear,
            queryModel,
            'SEO_KEY'
        );
        if (vehicleYmm) return Promise.resolve(vehicleYmm.model);
        else return Promise.resolve('');
    };
    const [marketName] = useServerData(
        'marketName',
        serverDataCallbackMarketName,
        [brand, currentRegionCode, currentLanguageRegionCode]
    );

    const [videoCategoriesResponse] = useServerData(
        'VideoCategoriesResponse',
        serverDataCallbackVideoCategories,
        [brand, currentRegionCode, currentLanguageRegionCode]
    );

    const vehicleYmmGroup = useAemVehicleModelData(
        brand,
        currentRegionCode,
        currentLanguageRegionCode,
        queryYear,
        queryModel,
        'SEO_KEY'
    );
    const { data: dyfEligible } = useDyfEligibleModel({
        year: queryYear,
        modelAlias: vehicleYmmGroup?.seoKey || '',
    });

    useSeoConfig('htv-library-page', 'dyf', [
        {
            fieldName: 'ymm',
            fieldValue: capitalizeYmm(
                `${queryYear} ` +
                    capitalizeYmm(brand) +
                    ' ' +
                    capitalizeYmm(marketName?.data || '')
            ),
        },
    ]);

    async function serverDataCallbackVideoCategories() {
        const vehicleYmm = await getAemVehicleModelData(
            brand,
            currentRegionCode,
            currentLanguageRegionCode,
            queryYear,
            queryModel,
            'SEO_KEY'
        );
        if (vehicleYmm && vehicleYmm.marketName) {
            const categoryVideos = await popularVideos.getVideosByCategories(
                queryYear,
                brand,
                vehicleYmm.marketName,
                currentLanguageRegionCode
            );
            if (categoryVideos) {
                for (const key in categoryVideos) {
                    categoryVideos[key].forEach(videoData => {
                        videoData.seoKey = vehicleYmm.seoKey;
                        videoData.custom_fields.year = vehicleYmm.year.toString();
                        videoData.custom_fields.model = vehicleYmm.marketName;
                    });
                }
            }
            return Promise.resolve(categoryVideos);
        } else {
            setCategoryVideosResponseError(true);
            return Promise.resolve([]);
        }
    }

    useFooterDisplay([videoCategoriesResponse]);

    const getUpdatedCanonical = () => {
        const baseUrl = siteMapService.getBaseUrl(
            currentLanguageRegionCode,
            brand
        );

        if (
            videoCategoriesResponse &&
            Object.keys(videoCategoriesResponse?.data || '').length > 0
        ) {
            return baseUrl.domain
                .concat(baseUrl.root)
                .concat(
                    siteMapService
                        .getPathFromRouterConfigs(
                            serverContext.currentLanguageRegionCode,
                            serverContext.brand,
                            'HTVVideoLibraryView',
                            [queryModel, queryYear]
                        )
                        ?.toString() || ''
                );
        } else {
            return baseUrl.domain
                .concat(baseUrl.root)
                .concat(`/vehicle/${queryModel}/${queryYear}/`);
        }
    };

    serverContext.canonical = filterXSS(getUpdatedCanonical());

    const currentVehicle: VehicleAttributes = {
        year: parseInt(queryYear, 10),
        make: brand,
        model: marketName?.data || '',
        ownerState: 1,
    };

    useEffect(() => {
        if (serverSideService.isClientSide()) {
            window.scrollTo(0, 0);
        }
    }, []);

    useEffect(() => {
        if (marketName?.data && !analyticsFired) {
            setAnalyticsFired(true);
            const dyfComponentName = {
                dyfComponentName: 'video library',
                ymm: {
                    year: parseInt(queryYear),
                    make: brand,
                    model: marketName?.data || '',
                },
            };
            fireEvents(
                'owner-htv-video-library-page',
                undefined,
                dyfComponentName
            );
        }
    }, []);

    useEffect(() => {
        const scriptB1eFuwPXkG = document.createElement('script');
        scriptB1eFuwPXkG.src = `https://players.brightcove.net/2379864788001/B1eFuwPXkG_default/index.min.js`;
        scriptB1eFuwPXkG.defer = true;
        document.body.appendChild(scriptB1eFuwPXkG);
        const scriptSyeO7cSQJf = document.createElement('script');
        scriptSyeO7cSQJf.src = `https://players.brightcove.net/4095259350001/SyeO7cSQJf_default/index.min.js`;
        scriptSyeO7cSQJf.defer = true;
        document.body.appendChild(scriptSyeO7cSQJf);
    }, [queryModel, queryYear]);

    const moreVehicleVideos = () => {
        fireEvents(
            'dyf-new-htv-search-another-vehicle-onclick-event',
            undefined,
            undefined
        );
    };
    const bannerTitle = useMemo(() => {
        if (currentLanguageRegionCode === 'fr-ca' && brand === 'lincoln') {
            return howToVideoContent?.htvLibrarylandingPageTitle.replace(
                '%vehicle%',
                capitalizeYmm(
                    capitalizeYmm(brand) +
                        ' ' +
                        capitalizeYmm(marketName?.data || '') +
                        ` ${queryYear}`
                )
            );
        } else
            return howToVideoContent?.htvLibrarylandingPageTitle.replace(
                '%vehicle%',
                capitalizeYmm(
                    `${queryYear} ` +
                        capitalizeYmm(brand) +
                        ' ' +
                        capitalizeYmm(marketName?.data || '')
                )
            );
    }, [
        howToVideoContent,
        queryYear,
        marketName,
        brand,
        currentLanguageRegionCode,
    ]);

    const bannerDescription = useMemo(() => {
        if (currentLanguageRegionCode === 'fr-ca' && brand === 'lincoln') {
            return howToVideoContent?.htvLibraryBannerDescription.replace(
                '%vehicle%',
                capitalizeYmm(
                    capitalizeYmm(brand) +
                        ' ' +
                        capitalizeYmm(marketName?.data || '') +
                        ` ${queryYear}`
                )
            );
        } else
            return howToVideoContent?.htvLibraryBannerDescription.replace(
                '%vehicle%',
                capitalizeYmm(
                    `${queryYear} ` +
                        capitalizeYmm(brand) +
                        ' ' +
                        capitalizeYmm(marketName?.data || '')
                )
            );
    }, [
        howToVideoContent,
        queryYear,
        marketName,
        brand,
        currentLanguageRegionCode,
    ]);

    const dyfFeatureGuideBreadcrumbsContent = useDyfFeatureGuideBreadcrumbsContent();

    const updatedDyfFeatureGuideBreadcrumbsContent = {
        ...dyfFeatureGuideBreadcrumbsContent,
        htvVideoLibraryBreadcrumb:
            howToVideoContent?.howToVideosBreadcrumbHowToVideos,
    };

    useFeatureGuideDaosAnalytics();
    return (
        <div className="htv-video-library-page" data-testid="htv-video-library">
            {howToVideoContent &&
                currentVehicle &&
                appInformationContent &&
                videoCategoriesResponse &&
                !categoryVideosResponseError && (
                    <>
                        <div>
                            <DyfFeatureGuideBreadCrumbs
                                origin={'videos'}
                                dyfFeatureGuideBreadcrumbs={
                                    updatedDyfFeatureGuideBreadcrumbsContent
                                }
                                vinDetails={null}
                                ymmDetails={serverContext.ymmDetails}
                            />
                        </div>
                        <ViewTemplate
                            className="htv-video-library"
                            hideLinksList={true}
                            page={page.current}
                            ctaBottomComponent={
                                <div
                                    className="banner"
                                    data-testid="htv-banner"
                                >
                                    <div className="htv-banner">
                                        <HowToBanner
                                            topComponent={
                                                howToVideoContent && (
                                                    <div
                                                        className="cta-video-text"
                                                        data-testid="htv-banner-component"
                                                    >
                                                        <h2 className="sub-title">
                                                            {
                                                                howToVideoContent.videoLibraryPageSubTitle
                                                            }
                                                        </h2>
                                                        <h1 className="title">
                                                            {bannerTitle}
                                                        </h1>
                                                        <div
                                                            className="paragraph"
                                                            tabIndex={0}
                                                        >
                                                            {bannerDescription}
                                                        </div>
                                                        <div
                                                            className="search-button"
                                                            data-testid="htv-banner-button"
                                                        >
                                                            <SecondaryButton
                                                                link={`${baseRoot}/how-to-videos/`}
                                                                className="vehicle-search-button"
                                                                onClick={
                                                                    moreVehicleVideos
                                                                }
                                                            >
                                                                {
                                                                    howToVideoContent.searchAnotherVehicleSecondaryButton
                                                                }
                                                            </SecondaryButton>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                            backgroundImageDesktop={''}
                                            backgroundImageMobile={''}
                                            bottomComponent={
                                                dyfEligible?.eligibleCountdown &&
                                                videoCategoriesResponse && (
                                                    <CountDown
                                                        vehicleAttributes={
                                                            currentVehicle
                                                        }
                                                    />
                                                )
                                            }
                                            vehicleAttributes={currentVehicle}
                                        />
                                        {videoCategoriesResponse &&
                                        Object.keys(
                                            videoCategoriesResponse?.data || ''
                                        ).length > 0 ? (
                                            <VideoLibraryCarousel
                                                videoCategories={
                                                    videoCategoriesResponse.data
                                                }
                                                data-testid="video-library-carousel"
                                            />
                                        ) : (
                                            howToVideoContent && (
                                                <div
                                                    className="library-empty"
                                                    tabIndex={0}
                                                    data-testid="empty-video-library-carousel"
                                                >
                                                    {
                                                        howToVideoContent.htvLibraryErrorDescription
                                                    }
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            }
                            backgroundImageDesktop={
                                process.env.REACT_APP_AEM_BASE_URL +
                                howToVideoContent.htvBackgroundImageDesktop
                            }
                            backgroundImageMobile={
                                process.env.REACT_APP_AEM_BASE_URL +
                                howToVideoContent.htvBackgroundImageMobile
                            }
                        />
                    </>
                )}
            {categoryVideosResponseError && <RoadBlockPage />}
        </div>
    );
};
export default HTVVideoLibraryView;
